import _ from "lodash";
import CustomFieldsColumn from "../../../../pagingfeed/column-helpers/custom_fields/CustomFieldsComponent";
import {
    auditPoliciesColumnConfig,
    deviceManagerDevicesColumnConfig,
    filesColumnConfig,
    fileServersColumnConfig,
    osPlansColumnConfig,
    schedulesColumnConfig,
} from "../../../../pagingfeed";
import {DATA_TYPE_NAMES} from "../widget-constants";

export const getEnabledCustomColumns = (
    customFieldColumns,
    customFieldsEntityDef,
    deviceCustomFields,
    columnConfig,
    columnConfigFromServer = {}
) => {
    const columnConfigMaxPriority = Math.max(
        ...Object.values(columnConfigFromServer).map(({priority}) => priority)
    );
    const customFieldOrder = deviceCustomFields.map(({id}) => id);

    return Object.entries(customFieldColumns)
        .reduce((customFields, [key, customField]) => {
            if (customField.enabled) {
                const newCustomField = customFieldOrder.includes(key)
                    ? {...customField, order: customFieldOrder.indexOf(key)}
                    : customField;
                return [...customFields, [key, newCustomField]];
            }
            return customFields;
        }, [])
        .sort(([, /* aKey */ aCustomField], [, /* bKey */ bCustomField]) =>
            // sort custom fields according to its 'order'
            customFieldsEntityDef
                .getSortingComparator("order")
                .call(null, aCustomField, bCustomField)
        )
        .map(([key, customField], idx) => {
            // build grid column object for each custom field
            const predefinedCustomField = _.find(deviceCustomFields, {id: key});
            const columnRendererComponent = _.get(
                predefinedCustomField,
                "columnRenderer",
                CustomFieldsColumn
            );
            const shownCustomField = (columnConfig[key] && columnConfig[key].shown) || false;
            const shownCustomFieldPriority =
                (columnConfig[key] && columnConfig[key].priority) || false;
            const customFieldColumn = {
                label: customField.label,
                description: customField.description || customField.label,
                entityColumn: false,
                shown: shownCustomField,
                path: `customFieldValues.${key}`,
                dataType: String(customField.dataType).toLowerCase(),
                typeInfo: customField.typeInfo || {},
                context: _.clone(customField),
                priority: shownCustomFieldPriority || columnConfigMaxPriority + idx + 1,
                columnRendererComponent,
            };
            const {labels, ids, multiSelect} = customFieldColumn.typeInfo || {};
            if (labels && ids && multiSelect !== true) {
                customFieldColumn.options = ids.reduce(
                    (options, id, idx) => Object.assign(options, {[id]: {title: labels[idx]}}),
                    {}
                );
            }
            return {[`customFieldValues.${key}`]: customFieldColumn};
        });
};

export function getSpreadedColumnConfig(
    columnConfig,
    {
        dataType,
        customFieldsEntityDef,
        customFieldColumns,
        osMap,
        fileServersMap,
        osPaths,
        data,
        deviceCustomFields,
    }
) {
    if (!_.isEmpty(columnConfig)) {
        const columnConfigCopy = _.cloneDeep(columnConfig);
        switch (dataType.value) {
            case DATA_TYPE_NAMES.DEVICES:
                _.assign(
                    columnConfigCopy,
                    ...getEnabledCustomColumns(
                        customFieldColumns,
                        customFieldsEntityDef,
                        deviceCustomFields,
                        columnConfig,
                        data.config.columnsConfig
                    )
                );
                break;
            case DATA_TYPE_NAMES.FILES:
                columnConfigCopy.pathId.context.osImagePaths = osPaths;
                break;
            default:
                break;
        }
        return columnConfigCopy;
    }

    return columnConfig;
}

export async function mergeSavedConfigToDefaultConfig(
    dataType,
    savedConfig,
    customFieldsEntityDef,
    customFieldColumns,
    osMap,
    fileServersMap,
    osPaths,
    data,
    deviceCustomFields
) {
    let defaultColumnsConfig = {};
    const commonProps = {
        dataType,
        customFieldsEntityDef,
        customFieldColumns,
        osMap,
        fileServersMap,
        osPaths,
        data,
        deviceCustomFields,
    };

    switch (dataType.value) {
        case DATA_TYPE_NAMES.AUDIT_POLICIES:
            defaultColumnsConfig = auditPoliciesColumnConfig;
            break;
        case DATA_TYPE_NAMES.DEVICES:
            const deviceColumnConfig = await deviceManagerDevicesColumnConfig.getColumnConfig();
            defaultColumnsConfig = getSpreadedColumnConfig(deviceColumnConfig, commonProps);
            break;
        case DATA_TYPE_NAMES.FILE_SERVERS:
            defaultColumnsConfig = fileServersColumnConfig;
            break;
        case DATA_TYPE_NAMES.FILES:
            defaultColumnsConfig = getSpreadedColumnConfig(filesColumnConfig, commonProps);
            break;
        case DATA_TYPE_NAMES.OS_PLANS:
            defaultColumnsConfig = osPlansColumnConfig;
            break;
        case DATA_TYPE_NAMES.SCHEDULES:
            defaultColumnsConfig = schedulesColumnConfig;
            break;
        default:
            defaultColumnsConfig = {};
    }

    const checkedSavedConfig = {};
    const defaultKeys = Object.keys(defaultColumnsConfig);

    defaultKeys.forEach((key) => {
        checkedSavedConfig[key] = savedConfig[key];
    });

    const defaultConfigDeepClone = _.cloneDeep(defaultColumnsConfig);
    const mergedColumnConfig = _.merge(defaultConfigDeepClone, checkedSavedConfig);

    const uuidPattern =
        "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$";

    if (dataType.value === DATA_TYPE_NAMES.DEVICES) {
        const filteredCustomFieldIds = Object.keys(mergedColumnConfig)
            .map((key) => {
                if (!_.isString(key)) {
                    return null;
                }
                if (key.startsWith("customFieldValues.")) {
                    return key.split("customFieldValues.")[1];
                }
                return !_.isEmpty(key.match(uuidPattern)) ? key : null;
            })
            .filter(Boolean);

        const customFieldIdsToRemove = _.difference(
            filteredCustomFieldIds,
            Object.keys(customFieldColumns)
        );

        if (customFieldIdsToRemove.length > 0) {
            customFieldIdsToRemove.forEach((customFieldId) => {
                delete mergedColumnConfig[customFieldId];
                delete mergedColumnConfig[`customFieldValues.${customFieldId}`];
            });
        }
    }

    return mergedColumnConfig;
}

export const getFilterQueryForDevices = (dataType, filter) => {
    return {
        $userQuery: filter.mongoQuery || filter,
        $systemQuery: {},
    };
};
